import type { JsonObject } from '../api/types';

/**
 * GET A/definition が返す manifest を、Task詳細パネルが引ける形へ正規化する。
 *
 * manifestの機械可読契約は docs/contracts/manifest.schema.json だが、
 * processKey・BPMN要素ID・taskKey にはpatternが無く、bindings のキーが
 * processes[].key と一致する保証もJSON Schemaには無い。
 * そのため「読めた分だけ返し、欠けている対応は呼び出し側が空として扱える」形にする。
 */

export type TaskKind = 'service' | 'human';
export type ConversationProvider = 'slack' | 'teams';

export interface TaskDeclaration {
  taskKey: string;
  kind: TaskKind;
  inputSchema: string | null;
  outputSchema: string | null;
  answerSchema: string | null;
  conversationProviders: ConversationProvider[];
}

export interface RpcDeclaration {
  method: string;
  inputSchema: string | null;
  outputSchema: string | null;
  mode: string;
}

/** binding は input/outputVariable 形式か inputMap/outputMap 形式のどちらか一方。 */
export type BindingMapping =
  | { style: 'single'; input: string; outputVariable: string }
  | { style: 'mapped'; inputMap: Record<string, string>; outputMap: Record<string, string> }
  | { style: 'unknown' };

export interface TaskBinding {
  processKey: string;
  /** BPMNの要素ID。表示名・taskKey・Pythonクラス名とは別物。 */
  activityId: string;
  taskKey: string;
  mapping: BindingMapping;
  /** 省略は全体公開を意味する。添付を含むTaskは指定されているべき。 */
  publicInputMap: Record<string, string> | null;
  publicOutputMap: Record<string, string> | null;
}

export interface ProcessDeclaration {
  key: string;
  path: string;
  inputSchema: string | null;
  startInputMap: Record<string, string> | null;
  resultMap: Record<string, string> | null;
}

export interface ManifestView {
  appKey: string;
  name: string;
  runtime: string;
  sdkVersion: string;
  processes: ProcessDeclaration[];
  decisionKeys: string[];
  tasks: Map<string, TaskDeclaration>;
  rpc: RpcDeclaration[];
  hasUi: boolean;
  hasConfiguration: boolean;
  allowsHttp: boolean;
  assetPaths: string[];
  databaseSchemaPath: string | null;
  /** processKey → activityId → binding。 */
  bindings: Map<string, Map<string, TaskBinding>>;
  /** 読み取れなかった箇所。画面に出して黙って落とさない。 */
  problems: string[];
}

/**
 * Task と RPC の登録簿は manifest ではなく、配備時の describe が返した宣言
 * （definition.declarations、MicroApplication クラスが持つ）から読む。
 */
export function parseManifest(
  raw: JsonObject | null | undefined,
  rawDeclarations: JsonObject | null | undefined = null,
): ManifestView {
  const problems: string[] = [];
  const manifest = raw ?? {};
  const declarations = rawDeclarations ?? {};

  const processes = asArray(manifest.processes).flatMap((entry, index) => {
    const record = asRecord(entry);
    const key = asString(record.key);
    const path = asString(record.path);
    if (key === null || path === null) {
      problems.push(`processes[${index}] の key/path を読めませんでした`);
      return [];
    }
    return [
      {
        key,
        path,
        inputSchema: asString(record.inputSchema),
        startInputMap: asStringMap(record.startInputMap),
        resultMap: asStringMap(record.resultMap),
      },
    ];
  });

  const tasks = new Map<string, TaskDeclaration>();
  for (const [taskKey, value] of Object.entries(asRecord(declarations.tasks))) {
    const record = asRecord(value);
    const kind = record.kind === 'human' ? 'human' : record.kind === 'service' ? 'service' : null;
    if (kind === null) {
      problems.push(`declarations.tasks["${taskKey}"] の kind を読めませんでした`);
      continue;
    }
    tasks.set(taskKey, {
      taskKey,
      kind,
      inputSchema: asString(record.inputSchema),
      outputSchema: asString(record.outputSchema),
      answerSchema: asString(record.answerSchema),
      conversationProviders: asArray(asRecord(record.conversation).providers).flatMap((provider) =>
        provider === 'slack' || provider === 'teams' ? [provider] : [],
      ),
    });
  }

  const rpc = Object.entries(asRecord(declarations.rpc)).flatMap(([method, value]) => {
    const record = asRecord(value);
    return [
      {
        method,
        inputSchema: asString(record.inputSchema),
        outputSchema: asString(record.outputSchema),
        mode: asString(record.mode) ?? 'read',
      },
    ];
  });

  const bindings = new Map<string, Map<string, TaskBinding>>();
  for (const [processKey, processBindings] of Object.entries(asRecord(manifest.bindings))) {
    const perActivity = new Map<string, TaskBinding>();
    for (const [activityId, value] of Object.entries(asRecord(processBindings))) {
      const record = asRecord(value);
      const taskKey = asString(record.taskKey);
      if (taskKey === null) {
        problems.push(`bindings["${processKey}"]["${activityId}"] に taskKey がありません`);
        continue;
      }
      perActivity.set(activityId, {
        processKey,
        activityId,
        taskKey,
        mapping: readMapping(record),
        publicInputMap: asStringMap(record.publicInputMap),
        publicOutputMap: asStringMap(record.publicOutputMap),
      });
      if (!tasks.has(taskKey)) {
        problems.push(
          `bindings["${processKey}"]["${activityId}"] が参照する taskKey "${taskKey}" が宣言（declarations.tasks）にありません`,
        );
      }
    }
    bindings.set(processKey, perActivity);
    if (!processes.some((process) => process.key === processKey)) {
      problems.push(`bindings のキー "${processKey}" に対応する processes がありません`);
    }
  }

  return {
    appKey: asString(manifest.appKey) ?? '',
    name: asString(manifest.name) ?? '',
    runtime: asString(manifest.runtime) ?? '',
    sdkVersion: asString(manifest.sdkVersion) ?? '',
    processes,
    decisionKeys: asArray(manifest.decisions).flatMap((entry) => {
      const key = asString(asRecord(entry).key);
      return key === null ? [] : [key];
    }),
    tasks,
    rpc,
    hasUi: asString(asRecord(manifest.ui).entry) !== null,
    hasConfiguration: asString(asRecord(manifest.configuration).schema) !== null,
    allowsHttp: asRecord(manifest.permissions).http === true,
    assetPaths: asArray(manifest.assets).flatMap((entry) => {
      const path = asString(asRecord(entry).path);
      return path === null ? [] : [path];
    }),
    databaseSchemaPath: asString(asRecord(manifest.database).schema),
    bindings,
    problems,
  };
}

/** BPMN要素を選んだときに、そのTaskの実装へ辿るための引き当て。 */
export function lookupTask(
  view: ManifestView,
  processKey: string,
  activityId: string,
): { binding: TaskBinding; declaration: TaskDeclaration | null } | null {
  const binding = view.bindings.get(processKey)?.get(activityId);
  if (!binding) return null;
  return { binding, declaration: view.tasks.get(binding.taskKey) ?? null };
}

/** Task種別に対応するPythonのメソッド。docs/microapp-package.md の契約。 */
export function pythonMethodsFor(declaration: TaskDeclaration): string[] {
  if (declaration.kind === 'service') return ['execute'];
  const methods = ['create_request', 'on_answer'];
  if (declaration.conversationProviders.length > 0) methods.push('on_action', 'on_message');
  return methods;
}

function readMapping(record: Record<string, unknown>): BindingMapping {
  const input = asString(record.input);
  const outputVariable = asString(record.outputVariable);
  if (input !== null && outputVariable !== null) {
    return { style: 'single', input, outputVariable };
  }
  const inputMap = asStringMap(record.inputMap);
  const outputMap = asStringMap(record.outputMap);
  if (inputMap !== null && outputMap !== null) {
    return { style: 'mapped', inputMap, outputMap };
  }
  return { style: 'unknown' };
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asStringMap(value: unknown): Record<string, string> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const result: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string') result[key] = entry;
  }
  return result;
}
